import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  CheckoutAddressData,
  CheckoutInitialData,
  CheckoutPaymentData,
  CheckoutPlan,
  CheckoutUserData,
} from '../types/CheckoutType';
import {
  createSubscription,
  createUser,
  fetchPublicPlans,
  getCachedPublicPlans,
  login,
} from '../api/checkout';
import { ApiError } from '../api/client';
import { saveLeadFromCheckout } from '../api/lead';
import { pushGTMEvent } from '../utils/gtm';

interface SubmitCheckoutParams {
  user: CheckoutUserData;
  address: CheckoutAddressData;
  payment: CheckoutPaymentData;
  plan: CheckoutPlan;
  installments?: number;
  isProfessional?: boolean;
}

interface CheckoutContextValue {
  plans: CheckoutPlan[];
  plansLoading: boolean;
  plansError: string | null;
  loading: boolean;
  error: string | null;
  initialData?: CheckoutInitialData;
  initialPlanId?: number;
  loadPublicPlans: () => Promise<void>;
  submitCheckout: (params: SubmitCheckoutParams) => Promise<void>;
  clearError: () => void;
}

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

interface CheckoutProviderProps {
  children: React.ReactNode;
  initialData?: CheckoutInitialData;
  initialPlanId?: number;
}

export const CheckoutProvider: React.FC<CheckoutProviderProps> = ({
  children,
  initialData,
  initialPlanId,
}) => {
  const cached = getCachedPublicPlans();
  const [plans, setPlans] = useState<CheckoutPlan[]>(cached ?? []);
  const [plansLoading, setPlansLoading] = useState(!cached);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const loadPublicPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const data = await fetchPublicPlans();
      if (data.length) {
        setPlans(data);
        setPlansError(null);
      } else if (!getCachedPublicPlans()?.length) {
        setPlansError('Nenhum plano disponível no momento.');
      }
    } catch (err) {
      // se já temos planos em cache, não assusta o usuário com erro
      if (!getCachedPublicPlans()?.length) {
        setPlansError(
          err instanceof ApiError
            ? `Não foi possível carregar os planos (${err.status}).`
            : 'Não foi possível carregar os planos.',
        );
      }
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPublicPlans();
  }, [loadPublicPlans]);

  const submitCheckout = useCallback(
    async ({ user, address, payment, plan, installments, isProfessional }: SubmitCheckoutParams) => {
      setLoading(true);
      setError(null);
      try {
        // Registra o lead assim que temos os dados de contato (não bloqueia o fluxo).
        saveLeadFromCheckout({
          name: user.full_name,
          email: user.email,
          phone: user.phone,
          source: window.location.href,
        });

        // 1. Conta — se o e-mail já existe, seguimos direto para o login.
        let emailExists = false;
        try {
          await createUser(user, !!isProfessional);
        } catch (err) {
          const msg = err instanceof ApiError ? err.message.toLowerCase() : '';
          if (!/existe|cadastrad|already|registrad/.test(msg)) throw err;
          emailExists = true;
        }

        // 2. Login para obter o token.
        let auth;
        try {
          auth = await login(user.email, user.password);
        } catch (err) {
          if (emailExists) {
            throw new ApiError(
              'Este e-mail já tem uma conta WashPass. Faça login no app para fazer a assinatura.',
              err instanceof ApiError ? err.status : 400,
            );
          }
          throw err;
        }

        // 3. Assinatura — sem veículo (o motorista cadastra depois, no app).
        await createSubscription(auth.access_token, {
          planId: plan.id,
          payment,
          address,
          installments,
        });

        // A escolha de perfil (pessoal/profissional) agora acontece antes da
        // conta existir, então esse evento só sai daqui — é o primeiro ponto
        // do fluxo novo em que já temos um user_id para o CRM.
        const userId = Number(auth.user_data.id) || null;
        if (userId != null) {
          pushGTMEvent({
            event: 'checkout_driver_type_selected',
            driver_type: isProfessional ? 'professional' : 'personal',
            user_id: userId,
            timestamp: new Date().toISOString(),
          });
        }

        pushGTMEvent({
          event: 'checkout_completed',
          plan_id: plan.id,
          plan_name: plan.name,
          plan_price: plan.price,
          plan_interval: plan.interval_type,
        });
        pushGTMEvent({ event: 'complete_registration' });
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Não foi possível concluir a assinatura.');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const value = useMemo(
    () => ({
      plans,
      plansLoading,
      plansError,
      loading,
      error,
      initialData,
      initialPlanId,
      loadPublicPlans,
      submitCheckout,
      clearError,
    }),
    [plans, plansLoading, plansError, loading, error, initialData, initialPlanId, loadPublicPlans, submitCheckout, clearError],
  );

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
};

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) throw new Error('useCheckout deve ser usado dentro de CheckoutProvider');
  return context;
};
